import { useState, type ReactElement } from 'react';
import { Plus } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { FetchErrorCallout } from '@/components/ui/fetch-error-callout';
import { Skeleton } from '@/components/ui/skeleton';
import { useActiveIdentifierTypes } from '@/hooks/useIdentifierTypes';

import {
  useFhirIdentifierMappingMutations,
  useFhirIdentifierMappings,
} from '../api';
import type {
  FhirIdentifierMappingRequest,
  FhirIdentifierMappingViewModel,
} from '../types';
import { DeleteMappingConfirmDialog } from './DeleteMappingConfirmDialog';
import { IdentifierMappingDialog } from './IdentifierMappingDialog';
import { IdentifierMappingsTable } from './IdentifierMappingsTable';

type DialogState =
  | { readonly mode: 'closed' }
  | { readonly mode: 'create' }
  | { readonly mode: 'edit'; readonly row: FhirIdentifierMappingViewModel };

export function IdentifierMappingsSection({
  integrationGuid,
}: {
  readonly integrationGuid: string;
}): ReactElement {
  const { t } = useTranslation();
  const mappingsQuery = useFhirIdentifierMappings(integrationGuid);
  const identifierTypesQuery = useActiveIdentifierTypes();
  const { create, update, remove } = useFhirIdentifierMappingMutations(integrationGuid);

  const [dialog, setDialog] = useState<DialogState>({ mode: 'closed' });
  const [deleteTarget, setDeleteTarget] = useState<FhirIdentifierMappingViewModel | null>(null);

  function closeDialog(): void {
    setDialog({ mode: 'closed' });
    create.reset();
    update.reset();
  }

  function handleSubmit(body: FhirIdentifierMappingRequest): void {
    if (dialog.mode === 'edit') {
      update.mutate(
        { guid: dialog.row.guid, body },
        { onSuccess: () => { closeDialog(); } },
      );
      return;
    }
    create.mutate(body, { onSuccess: () => { closeDialog(); } });
  }

  function handleConfirmDelete(): void {
    if (deleteTarget === null) return;
    remove.mutate(deleteTarget.guid, {
      onSuccess: () => {
        setDeleteTarget(null);
        remove.reset();
      },
    });
  }

  const rows = mappingsQuery.data ?? [];

  return (
    <section className="space-y-3">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h2 className="text-sm font-semibold">
            {t('routes.admin.fhirIntegrations.mappings.heading')}
          </h2>
          <p className="text-xs text-muted-foreground">
            {t('routes.admin.fhirIntegrations.mappings.description')}
          </p>
        </div>
        <Button
          type="button"
          size="sm"
          onClick={() => {
            create.reset();
            update.reset();
            setDialog({ mode: 'create' });
          }}
        >
          <Plus className="h-4 w-4" aria-hidden="true" />
          {t('routes.admin.fhirIntegrations.mappings.addButton')}
        </Button>
      </div>

      {mappingsQuery.isPending && (
        <div className="space-y-2" aria-hidden="true">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-10 w-full rounded-md" />
          ))}
        </div>
      )}

      {mappingsQuery.isError && (
        <div className="max-w-xl">
          <FetchErrorCallout
            error={mappingsQuery.error}
            heading={t('routes.admin.fhirIntegrations.mappings.error.heading')}
            message={t('routes.admin.fhirIntegrations.mappings.error.message')}
            codeLabel={t('routes.admin.fhirIntegrations.mappings.error.codeLabel')}
            onRetry={() => {
              mappingsQuery.refetch().catch(() => undefined);
            }}
            retryLabel={t('routes.admin.fhirIntegrations.mappings.error.retry')}
          />
        </div>
      )}

      {mappingsQuery.isSuccess && rows.length === 0 && (
        <p className="text-sm text-muted-foreground" role="status">
          {t('routes.admin.fhirIntegrations.mappings.empty')}
        </p>
      )}

      {mappingsQuery.isSuccess && rows.length > 0 && (
        <IdentifierMappingsTable
          rows={rows}
          onEdit={(row) => {
            create.reset();
            update.reset();
            setDialog({ mode: 'edit', row });
          }}
          onDelete={(row) => {
            remove.reset();
            setDeleteTarget(row);
          }}
          pendingDeleteGuid={remove.isPending ? (deleteTarget?.guid ?? null) : null}
        />
      )}

      <IdentifierMappingDialog
        open={dialog.mode === 'create' || dialog.mode === 'edit'}
        mode={dialog.mode === 'edit' ? 'edit' : 'create'}
        initial={dialog.mode === 'edit' ? dialog.row : null}
        identifierOptions={identifierTypesQuery.data ?? []}
        identifierOptionsLoading={identifierTypesQuery.isPending}
        onOpenChange={(next) => {
          if (!next) closeDialog();
        }}
        onSubmit={handleSubmit}
        pending={create.isPending || update.isPending}
        submitError={dialog.mode === 'edit' ? update.error : create.error}
      />

      <DeleteMappingConfirmDialog
        mapping={deleteTarget}
        onOpenChange={(next) => {
          if (!next && !remove.isPending) {
            setDeleteTarget(null);
            remove.reset();
          }
        }}
        onConfirm={handleConfirmDelete}
        pending={remove.isPending}
        error={remove.error}
      />
    </section>
  );
}
